import { TASK_STATUS_LABELS, type TaskStatus } from "@quovy/types";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../primitives/tooltip";
import { cn } from "../../utils";

const PIE_FILL_FRACTIONS: Record<TaskStatus, number> = {
  triage: 0,
  backlog: 0,
  todo: 0.25,
  in_progress: 0.5,
  in_review: 0.75,
  blocked: 0.5,
  done: 1,
  canceled: 1,
};

const STATUS_COLOR_CLASSES: Record<TaskStatus, string> = {
  triage: "text-amber-600 dark:text-amber-400",
  backlog: "text-zinc-500 dark:text-zinc-400",
  todo: "text-sky-600 dark:text-sky-400",
  in_progress: "text-blue-600 dark:text-blue-400",
  in_review: "text-violet-600 dark:text-violet-400",
  blocked: "text-rose-600 dark:text-rose-400",
  done: "text-emerald-600 dark:text-emerald-400",
  canceled: "text-zinc-500 dark:text-zinc-400",
};

const DASHED_RING_STATUSES = new Set<TaskStatus>(["triage", "backlog"]);
const CENTER = 8;
const OUTER_RADIUS = 6.5;
const PIE_RADIUS = 2.25;
const OUTER_CIRCUMFERENCE = 2 * Math.PI * OUTER_RADIUS;
const PIE_CIRCUMFERENCE = 2 * Math.PI * PIE_RADIUS;

export interface TaskStatusIconProps {
  status: TaskStatus;
  working?: boolean;
  className?: string;
  /** Replaces the task status wording when the icon stands in for another kind of status (threads). */
  label?: string;
  /** Off inside a control that has its own tooltip, so the two never stack. */
  tooltip?: boolean;
}

export function TaskStatusIcon({
  status,
  working = false,
  className,
  label: labelOverride,
  tooltip = true,
}: TaskStatusIconProps) {
  const fillFraction = PIE_FILL_FRACTIONS[status];
  const label = `${labelOverride ?? TASK_STATUS_LABELS[status]}${working ? ", working" : ""}`;

  const icon = (
    <svg
      viewBox="0 0 16 16"
      role="img"
      aria-label={label}
      className={cn("size-3.5 shrink-0", STATUS_COLOR_CLASSES[status], className)}
    >
      <circle
        cx={CENTER}
        cy={CENTER}
        r={OUTER_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={1}
        strokeOpacity={working ? 0.25 : 0.6}
        strokeDasharray={DASHED_RING_STATUSES.has(status) ? "2 1.5" : undefined}
      />
      {fillFraction > 0 && (
        <circle
          cx={CENTER}
          cy={CENTER}
          r={PIE_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={PIE_RADIUS * 2}
          strokeDasharray={`${fillFraction * PIE_CIRCUMFERENCE} ${PIE_CIRCUMFERENCE}`}
          transform="rotate(-90 8 8)"
        />
      )}
      {working && (
        <circle
          cx={CENTER}
          cy={CENTER}
          r={OUTER_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeDasharray={`${OUTER_CIRCUMFERENCE * 0.3} ${OUTER_CIRCUMFERENCE}`}
          className="animate-arc-spin"
        />
      )}
    </svg>
  );
  if (!tooltip) return icon;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{icon}</TooltipTrigger>
        <TooltipContent side="top" sideOffset={4}>
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
